/**
 * 納品件数の入力(月の一覧ページと日別の入力ポップアップ)で共有する型と計算。
 *  - CompanyEdit: 会社ごとの入力欄の文字列(取得件数・納品件数・人件費・交通費 × 出勤形態×報酬区分)
 *  - CompanyInfo: 日次集計から作る自動計算値(取得件数・人件費・交通費)。入力欄の初期値と「戻す」に使う
 *  - buildDetail: 入力欄 → deliveries の内訳(検証つき)
 */
import { COST_STYLES, COST_STYLE_LABELS, DELIVERY_STYLES, type CostStyle, type DeliveryDetail, type DeliveryStyle, type StaffDaily } from '@core/lib/types';

/** 人件費・交通費の内訳キー(管理者は現行シートどおり経費「報酬」側なので除く) */
export function costStyleOf(r: Pick<StaffDaily, 'work_style' | 'pay_type'>): CostStyle {
  if (r.work_style === 'timee') return 'timee';
  if (r.work_style === 'zaitaku') return r.pay_type === 'commission' ? 'buai' : 'jikyu';
  return 'shinagawa';
}

/** 納品件数の入力キー(タイミーの納品は品川に含める) */
export const deliveryKeyOf = (k: CostStyle): DeliveryStyle | null => (k === 'timee' ? null : k);

export type CompanyEdit = {
  /** 取得件数(自動値を初期表示。直すと赤字で保存) */
  appointments: Record<DeliveryStyle, string>;
  delivered: Record<DeliveryStyle, string>;
  labor: Record<CostStyle, string>;
  commute: Record<CostStyle, string>;
};
export const emptyCompanyEdit = (): CompanyEdit => ({
  appointments: { shinagawa: '', buai: '', jikyu: '' },
  delivered: { shinagawa: '', buai: '', jikyu: '' },
  labor: { shinagawa: '', buai: '', jikyu: '', timee: '' },
  commute: { shinagawa: '', buai: '', jikyu: '', timee: '' },
});

/** 日次集計から作る会社ごとの自動計算値 */
export type OpInfo = { op_id: string; name: string; cost: CostStyle; appts: number; labor: number; commute: number };
export type CompanyInfo = { ops: OpInfo[]; appts: Record<DeliveryStyle, number>; labor: Record<CostStyle, number>; commute: Record<CostStyle, number> };
export const emptyCompanyInfo = (): CompanyInfo => ({ ops: [], appts: { shinagawa: 0, buai: 0, jikyu: 0 }, labor: { shinagawa: 0, buai: 0, jikyu: 0, timee: 0 }, commute: { shinagawa: 0, buai: 0, jikyu: 0, timee: 0 } });

export function num(s: string | undefined): number | null {
  const t = (s ?? '').replace(/[,，\s円]/g, '').replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0));
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}
export const val = (s: string): number => {
  const n = num(s);
  return n === null || Number.isNaN(n) ? 0 : n;
};

export const sumCompany = (e: CompanyEdit) => ({
  appointments: DELIVERY_STYLES.reduce((s, k) => s + val(e.appointments[k]), 0),
  delivered: DELIVERY_STYLES.reduce((s, k) => s + val(e.delivered[k]), 0),
  byStyle: { shinagawa: val(e.delivered.shinagawa), buai: val(e.delivered.buai), jikyu: val(e.delivered.jikyu) } as Record<DeliveryStyle, number>,
  labor: COST_STYLES.reduce((s, k) => s + val(e.labor[k]), 0),
  commute: COST_STYLES.reduce((s, k) => s + val(e.commute[k]), 0),
});

/** 入力欄の初期値: 保存済みの内訳。取得件数・人件費・交通費は未保存なら自動計算を入れておく(⑩・⑬指示) */
export function initialEdit(det: DeliveryDetail | undefined, legacyCount: number | undefined, ci: CompanyInfo | undefined): CompanyEdit {
  const e = emptyCompanyEdit();
  for (const k of DELIVERY_STYLES) {
    e.delivered[k] = det?.by_style[k] !== undefined ? String(det.by_style[k]) : '';
    e.appointments[k] = det?.appointments?.[k] !== undefined ? String(det.appointments[k]) : ci && ci.appts[k] > 0 ? String(ci.appts[k]) : '';
  }
  if (!det && legacyCount !== undefined) e.delivered.jikyu = String(legacyCount);
  for (const k of COST_STYLES) {
    e.labor[k] = det?.labor?.[k] !== undefined ? String(det.labor[k]) : ci && ci.labor[k] > 0 ? String(ci.labor[k]) : '';
    e.commute[k] = det?.commute?.[k] !== undefined ? String(det.commute[k]) : ci && ci.commute[k] > 0 ? String(ci.commute[k]) : '';
  }
  return e;
}

/** その会社の日に出す出勤形態×報酬区分の行(自動計算・入力・稼働のあるもの。全区分表示なら全部) */
export function stylesFor(ci: CompanyInfo | undefined, e: CompanyEdit, showAll: boolean): CostStyle[] {
  return COST_STYLES.filter((k) => {
    const dk = deliveryKeyOf(k);
    return showAll || (ci?.labor[k] ?? 0) > 0 || (ci?.commute[k] ?? 0) > 0 || (ci?.ops.some((o) => o.cost === k) ?? false) || e.labor[k] !== '' || e.commute[k] !== '' || (dk !== null && (e.delivered[dk] !== '' || (ci?.appts[dk] ?? 0) > 0));
  });
}

/** 入力欄 → 保存する内訳。何も入っていなければ null。取得件数は自動値と同じなら保存しない(手修正した値だけ残す) */
export function buildDetail(dateLabel: string, companyName: string, e: CompanyEdit, ci: CompanyInfo | undefined): DeliveryDetail | null {
  const d: DeliveryDetail = { by_style: {}, client_appointments: 0, cancel_zaitaku: 0, cancel_shukkin: 0 };
  let any = false;
  for (const k of DELIVERY_STYLES) {
    const v = e.delivered[k].trim();
    if (v !== '') {
      const n = num(v);
      if (n === null || !Number.isInteger(n) || n < 0) throw new Error(`${dateLabel} ${companyName} ${COST_STYLE_LABELS[k]} の納品件数は0以上の整数で入力してください`);
      d.by_style[k] = n;
      any = true;
    }
    const a = e.appointments[k].trim();
    if (a !== '') {
      const n = num(a);
      if (n === null || !Number.isInteger(n) || n < 0) throw new Error(`${dateLabel} ${companyName} ${COST_STYLE_LABELS[k]} の取得件数は0以上の整数で入力してください`);
      if (n !== (ci?.appts[k] ?? 0)) {
        d.appointments = { ...(d.appointments ?? {}), [k]: n };
        any = true;
      }
    } else if ((ci?.appts[k] ?? 0) > 0) {
      d.appointments = { ...(d.appointments ?? {}), [k]: 0 };
      any = true;
    }
  }
  for (const src of ['labor', 'commute'] as const) {
    const out: Partial<Record<CostStyle, number>> = {};
    for (const k of COST_STYLES) {
      const v = e[src][k].trim();
      if (v === '') continue;
      const n = num(v);
      if (n === null || !Number.isInteger(n) || n < 0) throw new Error(`${dateLabel} ${companyName} ${COST_STYLE_LABELS[k]} の${src === 'labor' ? '人件費' : '交通費'}は0以上の整数(円)で入力してください`);
      out[k] = n;
      any = true;
    }
    if (Object.keys(out).length > 0) d[src] = out;
  }
  return any ? d : null;
}
